export interface PaginatedContent<T> {
    content?: T[] | null
    page?: number
    size?: number
    total_elements?: number
    total_pages?: number
}

interface PaginationDecisionParams<T> {
    requestedPage: number
    pageSize: number
    loadedCount: number
    pageData?: PaginatedContent<T> | null
    seenSignatures: ReadonlySet<string>
    getItemKey: (item: T) => string | number
}

export type PaginationDecision =
    | {type: "done"}
    | {type: "continue"; nextPage: number; signature: string}
    | {type: "error"; reason: "empty-page" | "repeated-page" | "invalid-page"}

export const getPaginationDecision = <T>({
    requestedPage,
    pageSize,
    loadedCount,
    pageData,
    seenSignatures,
    getItemKey
}: PaginationDecisionParams<T>): PaginationDecision => {
    if (!pageData || requestedPage < 1 || pageSize < 1) {
        return {type: "error", reason: "invalid-page"}
    }

    const content = pageData.content || []
    const hasTotalElements = Number.isFinite(pageData.total_elements)
    const hasTotalPages = Number.isFinite(pageData.total_pages)
    const totalElements = hasTotalElements ? Math.max(0, pageData.total_elements || 0) : null
    const totalPages = hasTotalPages
        ? Math.max(1, pageData.total_pages || 1)
        : totalElements !== null ? Math.max(1, Math.ceil(totalElements / pageSize)) : null
    const loadedAfterPage = loadedCount + content.length

    if (!content.length) {
        return totalElements === null
            || loadedAfterPage >= totalElements
            || (totalPages !== null && requestedPage >= totalPages)
            ? {type: "done"}
            : {type: "error", reason: "empty-page"}
    }

    const signature = content.map((item) => String(getItemKey(item))).join("|")
    if (!signature || seenSignatures.has(signature)) {
        return {type: "error", reason: "repeated-page"}
    }

    if ((totalElements !== null && loadedAfterPage >= totalElements)
        || (totalPages !== null && requestedPage >= totalPages)
        || (totalElements === null && totalPages === null && content.length < pageSize)) {
        return {type: "done"}
    }

    return {
        type: "continue",
        nextPage: requestedPage + 1,
        signature
    }
}
